import { logError } from '@/utils/logger';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeSlash, Lock, EnvelopeSimple, User, ArrowRight } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const GAZELLE_LOGO = "https://customer-assets.emergentagent.com/job_security-ops-hub-10/artifacts/gm0w9z4f_FLAVIGON.png";

const Login = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [serverReady, setServerReady] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Wake up server immediately when login page loads
  useEffect(() => {
    const wakeUp = async () => {
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health`, { mode: 'no-cors' });
        setServerReady(true);
      } catch (err) {
        console.warn('Backend en cours de démarrage, nouvelle tentative...', err?.message);
        // Retry once
        setTimeout(async () => {
          try {
            await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health`, { mode: 'no-cors' });
          } catch { /* silent */ }
          setServerReady(true);
        }, 2000);
      }
    };
    wakeUp();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLogin && !termsAccepted) {
      toast.error('Vous devez accepter la politique de confidentialité pour créer un compte');
      return;
    }
    setLoading(true);

    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : { email: formData.email, password: formData.password, name: formData.name };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const user = await response.json();
        sessionStorage.setItem('gz_auth', 'true');
        toast.success(isLogin ? 'Connexion réussie' : 'Compte créé');
        navigate('/dashboard', { replace: true, state: { user } });
      } else {
        const error = await response.json();
        const detail = error.detail;
        const message = typeof detail === 'string' ? detail
          : Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join(' ')
          : 'Identifiants incorrects';
        toast.error(message);
      }
    } catch (error) {
      logError('Auth error:', error);
      toast.error('Impossible de joindre le serveur. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Subtle background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-10" />
      <div className="absolute top-1/4 -left-32 w-64 h-64 bg-[#2563EB]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 -right-32 w-64 h-64 bg-[#2563EB]/5 rounded-full blur-3xl" />

      <div className="relative w-full max-w-[420px]">
        <div className="bg-[#0F172A] border border-[#1E293B] rounded-2xl p-8 shadow-2xl shadow-black/50">
          {/* Logo & Brand */}
          <div className="flex flex-col items-center mb-8">
            <div className="relative">
              <img src={GAZELLE_LOGO} alt="Gazelle Security" className="h-20 w-20 object-contain" />
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-[#10B981] rounded-full border-2 border-[#0F172A]" />
            </div>
            <h1 className="text-2xl font-bold text-[#F8FAFC] tracking-tight mt-4">Gazelle Security</h1>
            <p className="text-[#64748B] text-xs mt-1 uppercase tracking-widest">Plateforme de gestion</p>
          </div>

          {/* Tab switcher */}
          <div className="flex bg-[#1E293B]/50 rounded-lg p-1 mb-6">
            <button
              type="button"
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${isLogin ? 'bg-[#2563EB] text-white shadow-md' : 'text-[#94A3B8] hover:text-white'}`}
              data-testid="tab-login"
            >
              Connexion
            </button>
            <button
              type="button"
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${!isLogin ? 'bg-[#2563EB] text-white shadow-md' : 'text-[#94A3B8] hover:text-white'}`}
              data-testid="tab-register"
            >
              Inscription
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            {!isLogin && (
              <div>
                <Label className="text-[#94A3B8] text-xs uppercase tracking-wider">Nom complet</Label>
                <div className="relative mt-1.5">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required={!isLogin}
                    placeholder="Votre nom"
                    className="bg-[#1E293B] border-[#334155] text-[#F8FAFC] pl-10 h-11 focus:border-[#2563EB] transition-colors"
                    data-testid="register-name-input"
                  />
                </div>
              </div>
            )}

            <div>
              <Label className="text-[#94A3B8] text-xs uppercase tracking-wider">Adresse email</Label>
              <div className="relative mt-1.5">
                <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="votre@email.com"
                  className="bg-[#1E293B] border-[#334155] text-[#F8FAFC] pl-10 h-11 focus:border-[#2563EB] transition-colors"
                  data-testid="login-email-input"
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <Label className="text-[#94A3B8] text-xs uppercase tracking-wider">Mot de passe</Label>
              <div className="relative mt-1.5">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  placeholder="Votre mot de passe"
                  className="bg-[#1E293B] border-[#334155] text-[#F8FAFC] pl-10 pr-10 h-11 focus:border-[#2563EB] transition-colors"
                  data-testid="login-password-input"
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#F8FAFC] transition-colors"
                >
                  {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {!isLogin && (
              <label className="flex items-start gap-2 text-xs text-[#94A3B8] cursor-pointer" data-testid="register-terms-label">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="h-4 w-4 mt-0.5 accent-[#2563EB]"
                  data-testid="register-terms-checkbox"
                />
                <span>
                  J&apos;ai lu et j&apos;accepte la{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#F59E0B] underline hover:text-[#F97316]">
                    politique de confidentialité
                  </a>{' '}
                  et les conditions d&apos;utilisation des données de Gazelle Security SARLU.
                </span>
              </label>
            )}

            <Button
              type="submit"
              disabled={loading || (!isLogin && !termsAccepted)}
              className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-medium h-11 text-sm mt-2 transition-all"
              data-testid="login-submit-button"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{animationDelay: '150ms'}} />
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{animationDelay: '300ms'}} />
                  </div>
                  Connexion...
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  {isLogin ? 'Se connecter' : 'Créer le compte'}
                  <ArrowRight size={16} weight="bold" />
                </div>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t border-[#1E293B] text-center">
            <p className="text-[#64748B] text-xs">SARLU Gazelle Security — Système sécurisé</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
